import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';
import MetricRow from '../../components/MetricRow/MetricRow';
import MetricCard from '../../components/MetricCard/MetricCard';
import Button from '../../components/Button/Button';
import styles from './Overview.module.css';

const Overview = () => {
  const [reportExporting, setReportExporting] = useState(false);

  const handleExportReport = () => {
    setReportExporting(true);
    setTimeout(() => {
      setReportExporting(false);
      alert('Platform summary report exported successfully as CSV.');
    }, 600);
  };

  const headerActions = (
    <>
      <Button
        variant="secondary"
        onClick={handleExportReport}
        disabled={reportExporting}
      >
        {reportExporting ? 'Exporting...' : 'Export report'}
      </Button>
      <Link to="/send-notification">
        <Button variant="primary">Send notification</Button>
      </Link>
    </>
  );

  return (
    <>
      <PageHeader
        title="Platform overview"
        subtitle="Tuesday, Sep 29 · everything that needs a decision today."
        actions={headerActions}
      />

      <MetricRow columns={5}>
        <MetricCard label="Researchers" value="148" subtext="32 on Pro" />
        <MetricCard label="Participants" value="12,480" subtext="8,960 verified" />
        <MetricCard label="Live studies" value="41" subtext="7 pending approval" />
        <MetricCard label="Incentives paid · Sep" value="₹18.4L" subtext="to participants" />
        <MetricCard label="Platform revenue · Sep" value="₹4.1L" subtext="fees + subscriptions" isGreen />
      </MetricRow>

      <div className={styles.contentGrid}>
        <section className={styles.attentionCard}>
          <div className={styles.attentionCardHeader}>
            <h2 className={styles.cardTitle}>Needs attention</h2>
          </div>

          <div>
            <Link to="/approvals" className={styles.attentionRow}>
              <span className={`${styles.attentionBadge} ${styles.badgeBlue}`}>7</span>
              <span className={styles.attentionInfo}>
                <span className={styles.attentionTitle}>Studies awaiting approval</span>
                <span className={styles.attentionDesc}>Oldest submitted 18 hours ago</span>
              </span>
              <span className={styles.attentionLink}>Open →</span>
            </Link>

            <Link to="/verification" className={styles.attentionRow}>
              <span className={`${styles.attentionBadge} ${styles.badgeBlue}`}>23</span>
              <span className={styles.attentionInfo}>
                <span className={styles.attentionTitle}>Documents to verify</span>
                <span className={styles.attentionDesc}>9 flagged by auto-check</span>
              </span>
              <span className={styles.attentionLink}>Open →</span>
            </Link>

            <Link to="/support" className={styles.attentionRow}>
              <span className={`${styles.attentionBadge} ${styles.badgeYellow}`}>12</span>
              <span className={styles.attentionInfo}>
                <span className={styles.attentionTitle}>Open support tickets</span>
                <span className={styles.attentionDesc}>2 past SLA</span>
              </span>
              <span className={styles.attentionLink}>Open →</span>
            </Link>

            <Link to="/safety" className={styles.attentionRow}>
              <span className={`${styles.attentionBadge} ${styles.badgeRed}`}>4</span>
              <span className={styles.attentionInfo}>
                <span className={styles.attentionTitle}>Fraud flags</span>
                <span className={styles.attentionDesc}>1 high severity</span>
              </span>
              <span className={styles.attentionLink}>Open →</span>
            </Link>

            <Link to="/failed-payouts" className={styles.attentionRow}>
              <span className={`${styles.attentionBadge} ${styles.badgeRed}`}>3</span>
              <span className={styles.attentionInfo}>
                <span className={styles.attentionTitle}>Failed payouts</span>
                <span className={styles.attentionDesc}>UPI inactive or name mismatch</span>
              </span>
              <span className={styles.attentionLink}>Open →</span>
            </Link>
          </div>
        </section>

        <div className={styles.rightColumn}>
          <section className={styles.chartCard}>
            <h2 className={styles.cardTitle}>Revenue, last 6 months</h2>
            <div className={styles.chartWrapper}>
              <div className={styles.chartCol}>
                <div title="Apr: ₹1.4L" className={styles.chartBar} style={{ height: '34%', opacity: 0.67 }}></div>
                <span className={styles.chartLabel}>Apr</span>
              </div>
              <div className={styles.chartCol}>
                <div title="May: ₹1.8L" className={styles.chartBar} style={{ height: '41%', opacity: 0.705 }}></div>
                <span className={styles.chartLabel}>May</span>
              </div>
              <div className={styles.chartCol}>
                <div title="Jun: ₹2.3L" className={styles.chartBar} style={{ height: '50%', opacity: 0.75 }}></div>
                <span className={styles.chartLabel}>Jun</span>
              </div>
              <div className={styles.chartCol}>
                <div title="Jul: ₹2.9L" className={styles.chartBar} style={{ height: '63%', opacity: 0.815 }}></div>
                <span className={styles.chartLabel}>Jul</span>
              </div>
              <div className={styles.chartCol}>
                <div title="Aug: ₹3.6L" className={styles.chartBar} style={{ height: '78%', opacity: 0.89 }}></div>
                <span className={styles.chartLabel}>Aug</span>
              </div>
              <div className={styles.chartCol}>
                <div title="Sep: ₹4.1L" className={styles.chartBar} style={{ height: '94%', opacity: 0.97 }}></div>
                <span className={styles.chartLabel}>Sep</span>
              </div>
            </div>
          </section>

          <section className={styles.activityCard}>
            <h2 className={styles.cardTitle}>Recent activity</h2>
            <div>
              <div className={styles.activityRow}>
                <span className={`${styles.activityDot} ${styles.dotBlue}`}></span>
                <span className={styles.activityText}>Insight Loop Research upgraded to <strong>Pro</strong></span>
                <span className={styles.activityTime}>12m</span>
              </div>
              <div className={styles.activityRow}>
                <span className={`${styles.activityDot} ${styles.dotGreen}`}></span>
                <span className={styles.activityText}>Payout batch #PB-0921 paid · ₹1,06,300</span>
                <span className={styles.activityTime}>40m</span>
              </div>
              <div className={styles.activityRow}>
                <span className={`${styles.activityDot} ${styles.dotBlue}`}></span>
                <span className={styles.activityText}>Footwear CLT approved by Ops</span>
                <span className={styles.activityTime}>1h</span>
              </div>
              <div className={styles.activityRow}>
                <span className={`${styles.activityDot} ${styles.dotRed}`}></span>
                <span className={styles.activityText}>Duplicate device flagged on 3 accounts</span>
                <span className={styles.activityTime}>2h</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Overview;
